import { ref, computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { downloadFile } from '/@utilities/files';
import { getInnsiktAppFeatureReports } from '/@features/innsikt/innsikt.api';
import type { ExportFeatures, Reports } from '/@features/innsikt/innsikt.api.ts';

export function useInnsiktExport(appFeature: ExportFeatures) {
  const query = useQuery({
    queryKey: ['innsikt', 'reports', { appFeature }],
    queryFn: () => getInnsiktAppFeatureReports(appFeature),
    enabled: false,
    retry: false,
    select: (data: Reports) => new Map(data.map((r) => [r.Guid, r])),
  });

  const _selectedReport = ref<Reports[number] | null>(null);
  const isLoading = computed(() => query.isLoading.value || !!_selectedReport.value);

  async function downloadSingle(name: string, tenantId: number | null = null) {
    const reports = (await query.refetch()).data;

    const report = reports?.values().next().value;

    if (!report) {
      return;
    }

    return download(report, name, tenantId);
  }

  async function download(report: Reports[number], name: string, tenantId: number | null = null) {
    if (!report) return;

    _selectedReport.value = report;

    let url = `innsikt/reports/${report.Guid}/download`;

    if (tenantId) url += `?tenantId=${tenantId}`;

    const d = new Date();

    let fileName = name;
    fileName += `_${d.getFullYear()}-${d.getMonth() + 1}-${d.getUTCDate()}`;
    fileName += `_${d.getHours()}-${d.getMinutes()}`;

    return downloadFile(url, `${fileName}.${report.Extension}`).finally(() => {
      _selectedReport.value = null;
    });
  }

  return {
    reports: query.data,
    fetchReports: query.refetch,
    isLoading,
    open,
    download,
    downloadSingle,
  };
}
